'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { showOrderRejectedToast } from '@/components/ui/notification-toast-helpers'
import { useAuth } from '@/features/auth/hooks'
import { tradeVenueToIconKey } from '@/lib/proto/util'
import { fetchOrderHistory, fetchTradeHistory } from '@/service/account-history.api'
import { useActiveAccountsScope, useUserAccounts } from '@/service/account.hook'
import { useResyncDumpTracker } from '@/service/runtime-health/use-resync-dump-tracker'
import { snapshotAndUpdateQueryOptions } from '@/service/snapshot-query-options'
import {
  applyFilledOrdersToPositions,
  capHistoryPerAccount,
  convertAlgosToHistoryItems,
  getOrderCompositeKey,
  isAlgoFunctionallyComplete,
  isOpenOrderItem,
  mergeAlgoStates,
  mergeAllOrders,
  mergeHistoryPage,
  mergeTrades,
  orderEventTimestamp,
  pruneAlgoStates,
  pruneUnifiedItems,
  selectNewlyRejectedOrders,
  shouldReportRejection,
  TERMINAL_ALGO_STATUSES,
} from '@/service/ws/account-order-merge'
import {
  type AccountScopedResponse,
  useOpenOrdersStream,
  useOrdersHistoryStream,
} from '@/service/ws/use-orders-stream'
import {
  accountDataLoadingAtom,
  algoStatesRawAtom,
  openOrdersRawAtom,
  orderHistoryPaginationAtom,
  orderHistoryRawAtom,
  ordersLoadedAccountIdsAtom,
  positionsRawAtom,
  type TradeHistoryItem,
  tradesLoadedAccountIdsAtom,
  tradesPaginationAtom,
  tradesRawAtom,
  triggerResyncAtom,
} from '@/state/account'
import { orderFilterAtom } from '@/state/history-filter'
import { Denomination, OrderStatus, OrderType, TradeList } from '@/types/generated/common/trading'
import { AccountDataStreamType, InstrumentKind, TradeVenue } from '@/types/generated/common/types'
import { UnifiedOrderItem, UnifiedOrderList } from '@/types/generated/public_api'

const dev = process.env.NODE_ENV !== 'production'

function accountIdOf(item: UnifiedOrderItem): string {
  return item.order?.accountId || item.algoState?.accountIds?.[0] || ''
}

/**
 * Owns open-orders + order-history (WS + REST) for every currently-active
 * account at once — one REST request instead of one per account, and one WS
 * channel (`accountIds: [...]`) instead of one per account, both the whole
 * point of this migration.
 *
 * The REST query and the batched WS channel share ownership of a few refs
 * here (`hasOrderHistoryDumpRef`, `wsBufferRef`, `processedFilledOrdersRef`,
 * `processedPageCountRef`) — this is load-bearing, not incidental: a WS delta
 * that lands while the REST seed is still in flight must survive REST's
 * first-page merge, not get silently replaced by it. The WS side additionally
 * needs its own per-account dump/delta tracker (`accountDumpTrackerRef`)
 * since one channel instance now receives interleaved messages for every
 * account in scope — see `createAccountDumpTracker`.
 */
function AccountOrdersCoordinator() {
  const { accountIds, scopeKey, isResolved, hasAccounts } = useActiveAccountsScope()
  const orderFilter = useAtomValue(orderFilterAtom)
  const { isAuthenticated } = useAuth()
  // showDisabled: a rejection can arrive for an account the user has since
  // disabled — still want the toast to name it rather than showing nothing.
  const { data: userAccountsData } = useUserAccounts({ showDisabled: true })
  const accountInfoById = useMemo(
    () =>
      new Map(
        (userAccountsData?.accounts ?? []).map((a) => [
          a.accountId,
          { name: a.name, exchange: tradeVenueToIconKey(a.tradeVenue) },
        ])
      ),
    [userAccountsData?.accounts]
  )

  const setOpenOrders = useSetAtom(openOrdersRawAtom)
  const setOrderHistory = useSetAtom(orderHistoryRawAtom)
  const setAlgoStates = useSetAtom(algoStatesRawAtom)
  const setPositions = useSetAtom(positionsRawAtom)
  const setPagination = useSetAtom(orderHistoryPaginationAtom)
  const setLoadedAccountIds = useSetAtom(ordersLoadedAccountIdsAtom)
  const triggerResync = useAtomValue(triggerResyncAtom)

  // REST first page landed for the *current* scopeKey. Gates whether a WS
  // message writes straight into orderHistoryRawAtom or gets queued in
  // wsBufferRef until the seed lands.
  const hasOrderHistoryDumpRef = useRef(false)
  // WS history items (order + terminal-algo) queued while REST hasn't
  // resolved for the current scope. Flushed into orderHistoryRawAtom on
  // REST's first page, and its open-status subset re-merged into
  // openOrdersRawAtom too — REST's first-page write REPLACES this scope's
  // existing open-orders rows (see the REST effect below), which would
  // otherwise silently wipe a genuinely new order that arrived over WS
  // during the fetch. openOrdersRawAtom itself is still written directly and
  // immediately on every WS message regardless of this buffer, for
  // responsiveness — the buffer's job is only to patch over what REST's
  // replace step might just have clobbered.
  const wsBufferRef = useRef<UnifiedOrderItem[]>([])
  // Dedup key already includes accountId (getOrderCompositeKey), so sharing
  // one Set across every account in scope is safe.
  // Mirrors `processedFilledOrdersRef`: the orders stream re-sends an order on
  // every subsequent frame, so without this one refusal would re-toast forever.
  const processedRejectedOrdersRef = useRef<Set<string>>(new Set())
  const processedFilledOrdersRef = useRef<Set<string>>(new Set())
  const processedPageCountRef = useRef(0)
  const prevAccountIdsRef = useRef<string[]>([])
  const prevIsAuthenticatedRef = useRef(isAuthenticated)
  // The batched WS channel now receives every account's messages interleaved
  // on one stream, so "is this the account's first message" has to be tracked
  // per account rather than with a single boolean.
  const {
    trackerRef: accountDumpTrackerRef,
    syncGeneration,
    noteDumped,
    resetTracker,
  } = useResyncDumpTracker('orders')

  // Reset REST merge state whenever the thing the REST query is *for*
  // changes — scope, filter, or explicit resync. Without this, a
  // [a,b] -> [a,b,c] scope change would silently skip page-merging: the
  // component never unmounts now, so processedPageCountRef would still be
  // >0 from the old scope's query.
  useEffect(() => {
    hasOrderHistoryDumpRef.current = false
    processedPageCountRef.current = 0
    // Do NOT clear wsBufferRef here — WS deltas arriving during the gap
    // between this change and the new REST query resolving must still be
    // preserved and flushed once the new first page lands.
  }, [scopeKey, orderFilter, triggerResync])

  // A fresh subscribe (scope change, resync, or reconnect) means the server
  // re-sends a fresh dump for every account on the new channel, including
  // ones already known under the old one.
  useEffect(() => {
    resetTracker()
  }, [scopeKey, triggerResync, resetTracker])

  useEffect(() => {
    const wasAuthenticated = prevIsAuthenticatedRef.current
    prevIsAuthenticatedRef.current = isAuthenticated
    if (!wasAuthenticated && isAuthenticated) {
      hasOrderHistoryDumpRef.current = false
      processedPageCountRef.current = 0
      wsBufferRef.current = []
      processedFilledOrdersRef.current = new Set()
      resetTracker()
    }
  }, [isAuthenticated, resetTracker])

  // Prune accounts that dropped out of scope (deactivated, removed). Runs
  // for both a partial shrink and the full-drop-to-zero case — same effect
  // covers both, no separate branch needed here (the zero-accounts case is
  // additionally guarded on first mount below, since this diff-based effect
  // alone can't clear atoms seeded before any account list was ever known).
  useEffect(() => {
    if (!isResolved) return
    const dropped = prevAccountIdsRef.current.filter((id) => !accountIds.includes(id))
    if (dropped.length > 0) {
      const droppedSet = new Set(dropped)
      setOpenOrders((prev) => pruneUnifiedItems(prev, droppedSet))
      setOrderHistory((prev) => pruneUnifiedItems(prev, droppedSet))
      setAlgoStates((prev) => pruneAlgoStates(prev, droppedSet))
      setLoadedAccountIds((prev) => {
        const next = { ...prev }
        for (const id of dropped) delete next[id]
        return next
      })
      wsBufferRef.current = pruneUnifiedItems(wsBufferRef.current, droppedSet)
    }
    prevAccountIdsRef.current = accountIds
  }, [accountIds, isResolved, setOpenOrders, setOrderHistory, setAlgoStates, setLoadedAccountIds])

  // Zero-active-accounts explicit clear — see account-ids-recursive-waterfall.md
  // Phase 1.3 for why the diff-based effect above alone can't cover this (no
  // "previous non-empty list" to diff against on first mount).
  useEffect(() => {
    if (isResolved && !hasAccounts) {
      setOpenOrders([])
      setOrderHistory([])
      setAlgoStates([])
      setLoadedAccountIds({})
    }
  }, [isResolved, hasAccounts, setOpenOrders, setOrderHistory, setAlgoStates, setLoadedAccountIds])

  const handleAccountMessage = useCallback(
    (accountId: string, data: UnifiedOrderList | undefined, isInitialDumpForAccount: boolean) => {
      if (!data?.items) {
        if (isInitialDumpForAccount) {
          setLoadedAccountIds((prev) => ({ ...prev, [accountId]: true }))
        }
        return
      }

      // Account-tag anything the server didn't stamp — per-item accountId
      // isn't consistently populated on every message.
      const items: UnifiedOrderItem[] = data.items.map((item) => ({
        order: item.order
          ? { ...item.order, accountId: item.order.accountId || accountId }
          : undefined,
        algoState: item.algoState
          ? {
              ...item.algoState,
              accountIds: item.algoState.accountIds.length
                ? item.algoState.accountIds
                : [accountId],
            }
          : undefined,
      }))

      const orderItems = items.filter((item) => item.order)
      const algoItems = items.filter((item) => item.algoState)
      const algoStates = algoItems.map((item) => item.algoState!)

      // Written directly and immediately regardless of REST completion, for
      // responsiveness — the wsBufferRef flush below exists purely as a
      // safety net against REST's own first-page replace, not as a gate on
      // this write.
      setOpenOrders((prev) => mergeAllOrders(prev, orderItems).filter(isOpenOrderItem))
      if (algoStates.length > 0) {
        setAlgoStates((prev) => mergeAlgoStates(prev, algoStates))
      }

      if (isInitialDumpForAccount) {
        setLoadedAccountIds((prev) => ({ ...prev, [accountId]: true }))
      } else {
        // Proactively sync positions when a closing order gets filled. The
        // positions WS stream should send its own update, but this handles
        // timing gaps where the fill arrives before the position delta.
        const newlyFilled = orderItems.filter((item) => {
          const order = item.order!
          return (
            order.status === OrderStatus.ORDER_STATUS_FILLED &&
            !processedFilledOrdersRef.current.has(getOrderCompositeKey(order))
          )
        })
        if (newlyFilled.length > 0) {
          newlyFilled.forEach((item) =>
            processedFilledOrdersRef.current.add(getOrderCompositeKey(item.order!))
          )
          setPositions((prev) => applyFilledOrdersToPositions(prev, newlyFilled))
        }
      }

      // Surface refusals. A venue that publishes an `OrderResponse` notification
      // already toasts via NotificationManager; one that only publishes the
      // unified Order (Toss) reached the table and nothing else, so the order
      // looked like it silently vanished. Both paths share the
      // `order-<id>-<status>` sonner id, so this never double-reports.
      //
      // Runs for the initial dump too - `shouldReportRejection` decides, because
      // an account with no open orders gets no dump message and would otherwise
      // swallow the user's first rejection. Selection still runs on the dump so
      // replayed rejections are marked seen and cannot toast later.
      const now = Date.now()
      selectNewlyRejectedOrders(orderItems, processedRejectedOrdersRef.current).forEach((order) => {
        if (!shouldReportRejection(order, isInitialDumpForAccount, now)) return
        const info = accountInfoById.get(order.accountId)
        showOrderRejectedToast(order, orderEventTimestamp(order) ?? now, info?.name, info?.exchange)
      })

      // Order history: include terminal algos as well.
      const terminalAlgoItems = convertAlgosToHistoryItems(
        algoItems.filter(
          (item) =>
            item.algoState &&
            (TERMINAL_ALGO_STATUSES.has(item.algoState.status) ||
              isAlgoFunctionallyComplete(item.algoState))
        )
      )
      const allHistoryItems = [...orderItems, ...terminalAlgoItems]
      if (allHistoryItems.length > 0) {
        if (!hasOrderHistoryDumpRef.current) {
          wsBufferRef.current = mergeAllOrders(wsBufferRef.current, allHistoryItems)
        } else {
          setOrderHistory((prev) => capHistoryPerAccount(mergeAllOrders(prev, allHistoryItems)))
        }
      }
    },
    [
      setOpenOrders,
      setAlgoStates,
      setOrderHistory,
      setPositions,
      setLoadedAccountIds,
      accountInfoById,
    ]
  )

  const handleOpenOrdersMessage = useCallback(
    (response: AccountScopedResponse<UnifiedOrderList> | undefined) => {
      if (!response) return
      const { accountId, data } = response
      // Before isInitialDump: a resync's fresh dump can beat the effect that
      // resets the tracker, and would otherwise be merged as a delta.
      syncGeneration()
      const isInitialDumpForAccount = accountDumpTrackerRef.current.isInitialDump(accountId)
      if (isInitialDumpForAccount) {
        accountDumpTrackerRef.current.markDumped(accountId)
        noteDumped(accountIds)
      }
      handleAccountMessage(accountId, data, isInitialDumpForAccount)
    },
    [handleAccountMessage, syncGeneration, noteDumped, accountDumpTrackerRef, accountIds]
  )

  const ordersWsPayload = useMemo(
    () => ({
      accountId: '',
      accountIds,
      dataType: AccountDataStreamType.ACCOUNT_DATA_STREAM_TYPE_ORDERS,
    }),
    [accountIds]
  )

  useOpenOrdersStream({
    connected: true,
    payload: ordersWsPayload,
    onMessage: handleOpenOrdersMessage,
  })

  // Fetch order history for the whole scope in one request. queryFn only
  // fetches + tags accountId — no atom writes here, so a request that
  // resolves for a scope the coordinator has since moved away from can't
  // merge stale data back in (the scopeKey check in the merge effect below
  // owns that).
  const ordersQuery = useInfiniteQuery({
    queryKey: ['orderHistory', scopeKey, orderFilter],
    enabled: isResolved && hasAccounts,
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const response = await fetchOrderHistory({
        accountId: '',
        accountIds,
        limit: 100,
        symbols: orderFilter?.symbols ?? [],
        currency: orderFilter?.currency ?? [],
        from: orderFilter?.from,
        to: pageParam ?? orderFilter?.to,
      })

      const items: UnifiedOrderItem[] = (response.items ?? []).flatMap((item) => {
        const accountId = item.order?.accountId || item.algoState?.accountIds?.[0]
        if (!accountId) {
          if (dev) {
            console.warn(
              '[AccountOrdersCoordinator] dropping order-history row with no resolvable accountId',
              item
            )
          }
          return []
        }
        return [
          {
            order: item.order ? { ...item.order, accountId } : undefined,
            algoState: item.algoState
              ? {
                  ...item.algoState,
                  accountIds: item.algoState.accountIds.length
                    ? item.algoState.accountIds
                    : [accountId],
                }
              : undefined,
          },
        ]
      })

      // Tag with the scope this page was fetched for — checked against the
      // coordinator's *current* scopeKey before this page is merged, so a
      // slow response for a scope the user has since moved away from is
      // discarded instead of resurrecting stale rows.
      return { ...response, items, scopeKey }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const items = lastPage.items ?? []
      if (items.length < 100) return undefined
      const oldestTs = items.reduce((min, item) => {
        const t = Number(item.order?.createdAt ?? item.algoState?.createdAt ?? 0)
        return t > 0 && t < min ? t : min
      }, Infinity)
      return oldestTs === Infinity ? undefined : String(oldestTs)
    },
    ...snapshotAndUpdateQueryOptions,
    retry: 1,
  })

  useEffect(() => {
    const pages = ordersQuery.data?.pages ?? []
    for (let i = processedPageCountRef.current; i < pages.length; i++) {
      const page = pages[i]
      if (page.scopeKey !== scopeKey) continue // stale response for an old scope

      const items = page.items ?? []
      const orderItems = items.filter((item) => item.order)
      const algoItems = items.filter((item) => item.algoState)
      const algoStates = algoItems.map((item) => item.algoState!)
      const algoAsOrders = convertAlgosToHistoryItems(algoItems)
      const isFirstPage = i === 0
      const scopeSet = new Set(accountIds)

      if (algoStates.length > 0) {
        setAlgoStates((prev) => mergeAlgoStates(prev, algoStates))
      }

      const allHistoryItems = [...orderItems, ...algoAsOrders]
      setOrderHistory((prev) =>
        capHistoryPerAccount(
          mergeHistoryPage(
            prev,
            allHistoryItems,
            mergeAllOrders,
            isFirstPage
              ? { scopeAccountIds: scopeSet, bufferedItems: wsBufferRef.current }
              : undefined
          )
        )
      )

      // Seed open orders from history for any items still in an open status,
      // same replace-then-flush-buffer policy as history above — the buffer
      // flush here specifically re-applies anything a WS delta already wrote
      // that this replace step would otherwise wipe.
      if (isFirstPage) {
        const openOrderItems = orderItems.filter(isOpenOrderItem)
        setOpenOrders((prev) =>
          mergeHistoryPage(prev, openOrderItems, mergeAllOrders, {
            scopeAccountIds: scopeSet,
            bufferedItems: wsBufferRef.current.filter(isOpenOrderItem),
          })
        )

        // First-page coverage check: warn (dev only) if an active account has
        // zero rows in a full first page — either genuinely empty, or a sign
        // the lossy multi-account `to`-based paging (see plan doc §1.8)
        // starved that account on this page.
        if (dev && items.length >= 100) {
          const covered = new Set(items.map(accountIdOf))
          const missing = accountIds.filter((id) => !covered.has(id))
          if (missing.length > 0) {
            console.warn(
              '[AccountOrdersCoordinator] first history page has zero rows for accounts',
              missing
            )
          }
        }

        hasOrderHistoryDumpRef.current = true
        wsBufferRef.current = []
      }
    }
    processedPageCountRef.current = pages.length
  }, [ordersQuery.data, scopeKey, accountIds, setOrderHistory, setOpenOrders, setAlgoStates])

  useEffect(() => {
    if (ordersQuery.isError) {
      console.error('Failed to fetch order history:', ordersQuery.error)
      hasOrderHistoryDumpRef.current = true
      wsBufferRef.current = []
    }
  }, [ordersQuery.isError, ordersQuery.error])

  const fetchNextOrdersPage = ordersQuery.fetchNextPage
  useEffect(() => {
    setPagination({
      fetchMore: () => fetchNextOrdersPage(),
      hasMore: ordersQuery.hasNextPage,
      isFetching: ordersQuery.isFetchingNextPage,
    })
  }, [fetchNextOrdersPage, ordersQuery.hasNextPage, ordersQuery.isFetchingNextPage, setPagination])

  return null
}

function buildTradeHistoryItems(
  accountId: string,
  trades: TradeList['trades']
): TradeHistoryItem[] {
  return trades.map((trade) => ({
    order: {
      orderId: trade.orderId,
      accountId: trade.accountId || accountId,
      symbol: trade.symbol,
      exchange: trade.exchange,
      tradeVenue: trade.tradeVenue ?? TradeVenue.TRADE_VENUE_UNSPECIFIED,
      venueSymbol: trade.id, // Use unique trade ID for the row key to prevent overwriting
      side: trade.side,
      // The fill's own order type; UNSPECIFIED when unknown. mergeTrades never
      // lets an UNSPECIFIED update overwrite a known type.
      orderType: trade.orderType,
      quantity: trade.quantity,
      price: trade.price,
      avgFillPrice: trade.price,
      filledQuantity: trade.quantity,
      // Trades carry no denomination; UNSPECIFIED keeps the legacy display.
      denomination: Denomination.DENOMINATION_UNSPECIFIED,
      status: OrderStatus.ORDER_STATUS_FILLED,
      updatedAt: trade.executedAt,
      createdAt: trade.executedAt,
      label: trade.label || 'trade',
      instrumentKind: InstrumentKind.INSTRUMENT_KIND_UNSPECIFIED,
      isLiquidation: !!trade.liquidation,
      isRebalance: false,
      timeInForce: '',
      reduceOnly: false,
    },
    fee: trade.fee,
    feeCurrency: trade.feeCurrency,
    liquidation: trade.liquidation,
    liquidityType: trade.liquidityType,
  }))
}

/**
 * Trades counterpart to `AccountOrdersCoordinator` — see there for the
 * shared-buffer rationale. No open-orders-equivalent side atom to protect
 * (trades have no "still open" concept) and no fill-detection step, so this
 * is simpler than the orders coordinator.
 */
function AccountTradesCoordinator() {
  const { accountIds, scopeKey, isResolved, hasAccounts } = useActiveAccountsScope()
  const { isAuthenticated } = useAuth()

  const setTrades = useSetAtom(tradesRawAtom)
  const setPagination = useSetAtom(tradesPaginationAtom)
  const setLoadedAccountIds = useSetAtom(tradesLoadedAccountIdsAtom)
  const triggerResync = useAtomValue(triggerResyncAtom)

  const hasTradesDumpRef = useRef(false)
  const wsBufferRef = useRef<TradeHistoryItem[]>([])
  const processedPageCountRef = useRef(0)
  const prevAccountIdsRef = useRef<string[]>([])
  const prevIsAuthenticatedRef = useRef(isAuthenticated)
  const {
    trackerRef: accountDumpTrackerRef,
    syncGeneration,
    noteDumped,
    resetTracker,
  } = useResyncDumpTracker('trades')

  useEffect(() => {
    hasTradesDumpRef.current = false
    processedPageCountRef.current = 0
  }, [scopeKey, triggerResync])

  useEffect(() => {
    resetTracker()
  }, [scopeKey, triggerResync, resetTracker])

  useEffect(() => {
    const wasAuthenticated = prevIsAuthenticatedRef.current
    prevIsAuthenticatedRef.current = isAuthenticated
    if (!wasAuthenticated && isAuthenticated) {
      hasTradesDumpRef.current = false
      processedPageCountRef.current = 0
      wsBufferRef.current = []
      resetTracker()
    }
  }, [isAuthenticated, resetTracker])

  useEffect(() => {
    if (!isResolved) return
    const dropped = prevAccountIdsRef.current.filter((id) => !accountIds.includes(id))
    if (dropped.length > 0) {
      const droppedSet = new Set(dropped)
      setTrades((prev) => pruneUnifiedItems(prev, droppedSet))
      setLoadedAccountIds((prev) => {
        const next = { ...prev }
        for (const id of dropped) delete next[id]
        return next
      })
      wsBufferRef.current = pruneUnifiedItems(wsBufferRef.current, droppedSet)
    }
    prevAccountIdsRef.current = accountIds
  }, [accountIds, isResolved, setTrades, setLoadedAccountIds])

  useEffect(() => {
    if (isResolved && !hasAccounts) {
      setTrades([])
      setLoadedAccountIds({})
    }
  }, [isResolved, hasAccounts, setTrades, setLoadedAccountIds])

  const handleAccountMessage = useCallback(
    (accountId: string, data: TradeList | undefined, isInitialDumpForAccount: boolean) => {
      if (!data?.trades) {
        if (isInitialDumpForAccount) {
          setLoadedAccountIds((prev) => ({ ...prev, [accountId]: true }))
        }
        return
      }

      const items = buildTradeHistoryItems(accountId, data.trades)

      if (!hasTradesDumpRef.current) {
        wsBufferRef.current = [...wsBufferRef.current, ...items]
      } else {
        setTrades((prev) => capHistoryPerAccount(mergeTrades(prev, items)))
      }

      if (isInitialDumpForAccount) {
        setLoadedAccountIds((prev) => ({ ...prev, [accountId]: true }))
      }
    },
    [setTrades, setLoadedAccountIds]
  )

  const handleTradesMessage = useCallback(
    (response: AccountScopedResponse<TradeList> | undefined) => {
      if (!response) return
      const { accountId, data } = response
      // Before isInitialDump: a resync's fresh dump can beat the effect that
      // resets the tracker, and would otherwise be merged as a delta.
      syncGeneration()
      const isInitialDumpForAccount = accountDumpTrackerRef.current.isInitialDump(accountId)
      if (isInitialDumpForAccount) {
        accountDumpTrackerRef.current.markDumped(accountId)
        noteDumped(accountIds)
      }
      handleAccountMessage(accountId, data, isInitialDumpForAccount)
    },
    [handleAccountMessage, syncGeneration, noteDumped, accountDumpTrackerRef, accountIds]
  )

  const tradesWsPayload = useMemo(
    () => ({
      accountId: '',
      accountIds,
      dataType: AccountDataStreamType.ACCOUNT_DATA_STREAM_TYPE_TRADES,
    }),
    [accountIds]
  )

  useOrdersHistoryStream({
    connected: true,
    payload: tradesWsPayload,
    onMessage: handleTradesMessage,
  })

  const tradesQuery = useInfiniteQuery({
    // Unfiltered: this list is shared by every Positions widget, and each applies its own
    // Trades filter to it on display (tradeHistoryFilterAtomFamily, history-table.tsx).
    queryKey: ['tradeHistory', scopeKey],
    enabled: isResolved && hasAccounts,
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const response = await fetchTradeHistory({
        accountId: '',
        accountIds,
        limit: 100,
        symbols: [],
        currency: [],
        to: pageParam,
      })
      return { ...response, scopeKey }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const trades = lastPage.trades ?? []
      if (trades.length < 100) return undefined
      const oldestTs = trades.reduce((min, trade) => {
        const t = Number(trade.executedAt ?? 0)
        return t > 0 && t < min ? t : min
      }, Infinity)
      return oldestTs === Infinity ? undefined : String(oldestTs)
    },
    ...snapshotAndUpdateQueryOptions,
    retry: 1,
  })

  useEffect(() => {
    const pages = tradesQuery.data?.pages ?? []
    for (let i = processedPageCountRef.current; i < pages.length; i++) {
      const page = pages[i]
      if (page.scopeKey !== scopeKey) continue

      const rawTrades = page.trades ?? []
      const items: TradeHistoryItem[] = rawTrades.flatMap((trade) => {
        if (!trade.accountId) {
          if (dev) {
            console.warn(
              '[AccountTradesCoordinator] dropping trade-history row with no accountId',
              trade
            )
          }
          return []
        }
        return buildTradeHistoryItems(trade.accountId, [trade])
      })
      const isFirstPage = i === 0
      const scopeSet = new Set(accountIds)

      setTrades((prev) =>
        capHistoryPerAccount(
          mergeHistoryPage(
            prev,
            items,
            mergeTrades,
            isFirstPage
              ? { scopeAccountIds: scopeSet, bufferedItems: wsBufferRef.current }
              : undefined
          )
        )
      )

      if (isFirstPage) {
        // First-page coverage check: warn (dev only) if an active account has
        // zero rows in a full first page — same lossy multi-account `to`-based
        // paging risk as order history (see plan doc §1.8).
        if (dev && rawTrades.length >= 100) {
          const covered = new Set(items.map(accountIdOf))
          const missing = accountIds.filter((id) => !covered.has(id))
          if (missing.length > 0) {
            console.warn(
              '[AccountTradesCoordinator] first trade-history page has zero rows for accounts',
              missing
            )
          }
        }

        hasTradesDumpRef.current = true
        wsBufferRef.current = []
      }
    }
    processedPageCountRef.current = pages.length
  }, [tradesQuery.data, scopeKey, accountIds, setTrades])

  useEffect(() => {
    if (tradesQuery.isError) {
      console.error('Failed to fetch trade history:', tradesQuery.error)
      hasTradesDumpRef.current = true
      wsBufferRef.current = []
    }
  }, [tradesQuery.isError, tradesQuery.error])

  const fetchNextTradesPage = tradesQuery.fetchNextPage
  useEffect(() => {
    setPagination({
      fetchMore: () => fetchNextTradesPage(),
      hasMore: tradesQuery.hasNextPage,
      isFetching: tradesQuery.isFetchingNextPage,
    })
  }, [fetchNextTradesPage, tradesQuery.hasNextPage, tradesQuery.isFetchingNextPage, setPagination])

  return null
}

/**
 * Mount once, alongside <AccountDataManager /> and <ActivePositionsSync />.
 * Sets the "orders" loading flag: cleared only once every currently-active
 * account has completed its first WS dump, not just the first one to finish
 * (loaded-flag semantics are unchanged from before this migration — still
 * driven entirely by the WS side, never by REST completion).
 */
export function ActiveOrdersSync() {
  const { accountIds } = useActiveAccountsScope()
  const loadedAccountIds = useAtomValue(ordersLoadedAccountIdsAtom)
  const setLoading = useSetAtom(accountDataLoadingAtom)

  useEffect(() => {
    const allLoaded = accountIds.length === 0 || accountIds.every((id) => loadedAccountIds[id])
    setLoading((prev) => (prev.orders === !allLoaded ? prev : { ...prev, orders: !allLoaded }))
  }, [accountIds, loadedAccountIds, setLoading])

  return <AccountOrdersCoordinator />
}

/** Trades counterpart to `ActiveOrdersSync` — see there. */
export function ActiveTradesSync() {
  const { accountIds } = useActiveAccountsScope()
  const loadedAccountIds = useAtomValue(tradesLoadedAccountIdsAtom)
  const setLoading = useSetAtom(accountDataLoadingAtom)

  useEffect(() => {
    const allLoaded = accountIds.length === 0 || accountIds.every((id) => loadedAccountIds[id])
    setLoading((prev) => (prev.trades === !allLoaded ? prev : { ...prev, trades: !allLoaded }))
  }, [accountIds, loadedAccountIds, setLoading])

  return <AccountTradesCoordinator />
}
